import type { ToastServiceMethods } from 'primevue/toastservice';
import { useToast } from 'primevue/usetoast';

import { track } from '@/helpers/analytics/mixpanel';

interface ToastMessage {
  severity?: 'success' | 'info' | 'warn' | 'error' | 'secondary';
  summary?: string;
  detail?: string;
  life?: number;
  closable?: boolean;
  group?: string;
  styleClass?: string;
  contentStyleClass?: string;
  errorCode?: string;
}

interface TrackedToastService extends Omit<ToastServiceMethods, 'add'> {
  add(message: ToastMessage): void;
}

// Track if we've already wrapped the toast service
let isWrapped = false;
let originalAddMethod: ((message: ToastMessage) => void) | null = null;

export function useTrackedToast(): TrackedToastService {
  const toast = useToast();

  // Only wrap once to prevent multiple tracking calls
  if (!isWrapped) {
    originalAddMethod = toast.add.bind(toast);
    
    toast.add = (message: ToastMessage) => {
      // Track error toasts in Mixpanel
      if (message.severity === 'error') {
        // Use document.title which is always current and set by the router
        const pageTitle = document.title || 'eRank';
        const errorMessage = message.detail || message.summary || 'Unknown error';
        const errorCode = message.errorCode || 'N/A';

        track('Received Error', {
          Type: 'Toast',
          'Page Title': pageTitle,
          'Error Message': errorMessage,
          'Error Code': errorCode,
        });
      }

      // Call the original add method
      return originalAddMethod!(message);
    };
    
    isWrapped = true;
  }

  return toast as TrackedToastService;
}

